import { ok } from 'neverthrow';
import type {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { WorkflowEditorToolbarActionBase } from './workflow-editor-toolbar-action.base';
import {
  workflowCanvasShortcutAvailable,
  workflowModelFromContext,
  type WorkflowClipboardOps,
} from './workflow-toolbar-action.helpers';

/**
 * Re-apply the last undone graph edit (Cmd/Ctrl+Shift+Z).
 *
 * The redo counterpart to {@link WorkflowUndoAction} — same framework wiring, the
 * work lives in {@link WorkflowModel.redo}. Shift+Z is matched exactly by the
 * shortcut layer, so it never collides with plain Cmd+Z (undo).
 */
export class WorkflowRedoAction extends WorkflowEditorToolbarActionBase {
  title = 'Redo';
  override description = 'Redo the last undone change to the graph';
  group = 'Workflow';
  icon = 'redo';
  shortcut = ['ctrl', 'shift', 'z'];

  constructor(private readonly ops: WorkflowClipboardOps) {
    super();
  }

  /** Leave the keystroke to a text field or a dialog stacked over the canvas. */
  shortcutAvailable(): boolean {
    return workflowCanvasShortcutAvailable(this.ops);
  }

  /** Only when there is an undone edit to redo — also hides it from the palette. */
  override applicable(context: ApplicableContext): boolean {
    const model = workflowModelFromContext(context);
    return model !== null && model.canRedoWatchable.snapshot();
  }

  override async execute(
    _params: Record<string, never>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    workflowModelFromContext(context)?.redo();
    return ok({});
  }
}
